import axios from 'axios';
import { BASE_URL } from '../api/config';
import type { ErreurValidation, EtatFormulaires, Instance, Modele, ModeleInput, ModeleResume } from '../formulaires/types';
import { creerApiFormulaires } from '../formulaires/api';
import type { ApiFormulaires } from '../formulaires/api';

// Service du menu « Formulaires » (modèles) et du panneau « Formulaires » d'un dossier (instances).
// URL exactes de `AdminVisaTravelApp backend/formulaires/routes.js` (préfixe /formulaires).
// Pas de try/catch ici : l'erreur axios remonte au composant → messageErreurs / erreursParChemin → toast / cartes.

// Tout ce que définissent src/formulaires/types.ts (types, constantes, fonctions pures) et src/formulaires/libelles.ts
// (phrases lisibles : decrireCondition, decrireType…) est ré-exporté : un composant peut tout importer depuis ce service.
export * from '../formulaires/types';
export * from '../formulaires/libelles';

const URL = `${BASE_URL}/formulaires`;

// ---------------------------------------------------------------------------
// Diagnostic
// ---------------------------------------------------------------------------

// Répond même tables absentes (ok=false + raison) ; force la vérification côté serveur
export const etatFormulaires = async (): Promise<EtatFormulaires> =>
  (await axios.get<EtatFormulaires>(`${URL}/etat`)).data;

// ---------------------------------------------------------------------------
// Modèles
// ---------------------------------------------------------------------------

// Modèles non supprimés, triés par nom ; actifs=true : seulement actif=1 (Select de « Nouveau dossier »)
export const listerModeles = async (actifs?: boolean): Promise<ModeleResume[]> =>
  (await axios.get<ModeleResume[]>(`${URL}/modeles${actifs ? '?actifs=1' : ''}`)).data;

export const getModele = async (id: number): Promise<Modele> =>
  (await axios.get<Modele>(`${URL}/modeles/${id}`)).data;

// 201 Modele ; 400 { message, erreurs: [{ chemin, message }] }
export const creerModele = async (d: ModeleInput): Promise<Modele> =>
  (await axios.post<Modele>(`${URL}/modeles`, d)).data;

// Mise à jour partielle ; version+1 si la définition change
export const majModele = async (id: number, d: Partial<ModeleInput>): Promise<Modele> =>
  (await axios.put<Modele>(`${URL}/modeles/${id}`, d)).data;

// Soft delete (204)
export const supprimerModele = async (id: number): Promise<void> => {
  await axios.delete(`${URL}/modeles/${id}`);
};

// 201 : copie « <nom> (copie) », version 1, inactive
export const dupliquerModele = async (id: number): Promise<Modele> =>
  (await axios.post<Modele>(`${URL}/modeles/${id}/dupliquer`, {})).data;

// ---------------------------------------------------------------------------
// Instances (formulaires joints à un dossier)
// ---------------------------------------------------------------------------

// Appels du panneau du dossier, de « Joindre un formulaire » et de « Nouveau dossier » (détail dans formulaires/api.ts) :
// les composants les lisent par ApiFormulairesContext, dont c'est la valeur par défaut. L'admin Legrand fournit la sienne.
export const apiFormulairesAdmin: ApiFormulaires = creerApiFormulaires(URL);

export const getInstance = async (id: number): Promise<Instance> =>
  (await axios.get<Instance>(`${URL}/dossiers/${id}`)).data;

// ---------------------------------------------------------------------------
// Erreurs
// ---------------------------------------------------------------------------

interface CorpsErreur {
  message?: string;
  erreurs?: (ErreurValidation | string)[];
  mail?: { envoye?: boolean; erreur?: string | null };
}

const corpsErreur = (e: unknown): CorpsErreur | null =>
  axios.isAxiosError(e) && e.response && e.response.data && typeof e.response.data === 'object'
    ? (e.response.data as CorpsErreur)
    : null;

// Code HTTP d'une erreur axios (403, 409, 503…) ou null (réseau, erreur non axios)
export const codeHttp = (e: unknown): number | null =>
  axios.isAxiosError(e) && e.response ? e.response.status : null;

// Message lisible d'une erreur axios du module : 400 de validation → 'chemin : message' joints par ' — ' ;
// 502 d'envoi → data.mail.erreur ; sinon data.message ; sinon `defaut`.
// (Ne pas réutiliser messageErreur d'AlertesScreen, typé erreurs: string[].)
export const messageErreurs = (e: unknown, defaut: string): string => {
  const data = corpsErreur(e);
  if (data) {
    if (Array.isArray(data.erreurs) && data.erreurs.length) {
      return data.erreurs
        .map((x) => (typeof x === 'string' ? x : x.chemin ? `${x.chemin} : ${x.message}` : x.message))
        .join(' — ');
    }
    if (data.mail && data.mail.erreur) return data.mail.erreur;
    if (data.message) return data.message;
  }
  return defaut;
};

// Erreurs 400 indexées par chemin ('nom', 'sections[0].questions[2].condition.valeur'…) pour l'affichage sur les
// cartes ; {} si l'erreur n'en porte pas. Un même chemin garde son premier message.
export const erreursParChemin = (e: unknown): Record<string, string> => {
  const r: Record<string, string> = {};
  const data = corpsErreur(e);
  if (data && Array.isArray(data.erreurs)) {
    data.erreurs.forEach((x) => {
      if (typeof x === 'object' && x && x.chemin && r[x.chemin] === undefined) r[x.chemin] = x.message;
    });
  }
  return r;
};

// Colonne JSON arrivée en chaîne (selon le driver) → objet ; null si illisible
export const parseJson = <T>(v: T | string | null | undefined): T | null => {
  if (v === null || v === undefined) return null;
  if (typeof v !== 'string') return v;
  try { return JSON.parse(v) as T; } catch { return null; }
};
